<template>
  <section class="s-contact inViewport">
    <Loader v-if="isLoading" display="absolute"></Loader>

    <div class="s-contact__conts">
      <div class="s-contact__row">
        <div class="s-contact__main">
          <div class="s-contact__txt">
            <div class="e-label">{{ label }}</div>
            <h2 class="s-contact__title">
              {{ title }}
            </h2>
            <div
              v-if="summary"
              class="s-contact__summary"
              v-html="summary"></div>
          </div>

          <div class="s-contact__cont">
            <div class="s-contact__form">
              <form
                v-show="!showResponse && !showFailResponse"
                ref="form"
                autocomplete="off"
                enctype="multipart/form-data"
                @submit.prevent="onSubmitForm">
                <div class="form-panel">
                  <div class="s-contact__form__heading">
                    <div class="h2 mb-04">
                      {{ dictionary.formTitle }}
                      <button
                        type="button"
                        class="close"
                        data-dismiss="modal"
                        aria-label="Close"
                        @click="closeContactModal">
                        <span aria-hidden="true"></span>
                      </button>
                    </div>
                    <div class="s-contact__form__desc">
                      {{ dictionary.formDesc }}
                    </div>
                  </div>
                  <div class="mb-3">
                    <label for="name">{{ dictionary.nameLabel }}</label>
                    <input
                      id="name"
                      v-model.trim="name"
                      type="text"
                      class="form-control"
                      :placeholder="dictionary.nameLabel"
                      :class="{ 'has-error': v$.name.$error }"
                      @blur="v$.name.$touch" />

                    <div v-if="v$.name.$error">
                      <div v-if="v$.name.required.$invalid" class="error">
                        {{ dictionary.nameRequired }}
                      </div>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="surname">
                      {{ dictionary.surnameLabel }}
                    </label>
                    <input
                      id="surname"
                      v-model.trim="surname"
                      type="text"
                      class="form-control"
                      :placeholder="dictionary.surnameLabel"
                      :class="{ 'has-error': v$.surname.$error }"
                      @blur="v$.surname.$touch" />

                    <div v-if="v$.surname.$error">
                      <div v-if="v$.surname.required.$invalid" class="error">
                        {{ dictionary.surnameRequired }}
                      </div>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="email">{{ dictionary.emailLabel }}</label>
                    <input
                      id="email"
                      v-model.trim="email"
                      type="email"
                      class="form-control"
                      :placeholder="dictionary.emailLabel"
                      :class="{ 'has-error': v$.email.$error }"
                      @blur="v$.email.$touch" />

                    <div v-if="v$.email.$error">
                      <div v-if="v$.email.required.$invalid" class="error">
                        {{ dictionary.emailRequired }}
                      </div>
                      <div v-if="v$.email.email.$invalid" class="error">
                        {{ dictionary.emailRegex }}
                      </div>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label for="phone">{{ dictionary.phoneLabel }}</label>
                    <input
                      id="phone"
                      v-model.trim="phone"
                      type="text"
                      class="form-control"
                      :placeholder="dictionary.phoneLabel"
                      :class="{ 'has-error': v$.phone.$error }"
                      @blur="v$.phone.$touch" />

                    <div v-if="v$.phone.$error">
                      <div v-if="v$.phone.required.$invalid" class="error">
                        {{ dictionary.emailRequired }}
                      </div>
                      <div v-if="v$.phone.numeric.$invalid" class="error">
                        {{ dictionary.phoneNumberNumeric }}
                      </div>
                    </div>
                  </div>

                  <div class="mb-32 mt-40">
                    <div class="form-check">
                      <input
                        id="terms"
                        v-model.trim="terms"
                        class="form-check-input"
                        type="checkbox"
                        :class="{ 'has-error': v$.terms.$error }"
                        @blur="v$.terms.$touch" />
                      <label class="form-check-label" for="terms">
                        <div v-html="dictionary.termsLabel"></div>
                      </label>

                      <div v-if="v$.terms.$error">
                        <div v-if="v$.terms.checked.$invalid" class="error">
                          {{ dictionary.termsRequired }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="row">
                    <div class="s-contact__cta">
                      <button
                        type="submit"
                        class="btn btn-secondary"
                        :disabled="isLoading || v$.$invalid">
                        <span>{{ dictionary.buttonSubmitText }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              <div v-if="showResponse" class="s-contact__msg form-panel">
                <span
                  class="s-contact__msg__icon s-contact__msg__icon--success"></span>
                <div class="s-contact__msg__title">
                  {{ dictionary.responseSuccessMesage }}
                </div>
                <div class="s-contact__msg__desc">
                  {{ dictionary.responseSuccessDescription }}
                </div>
              </div>

              <div v-if="showFailResponse" class="s-contact__msg form-panel">
                <span
                  class="s-contact__msg__icon s-contact__msg__icon--fail"></span>
                <div class="s-contact__msg__title">
                  {{ dictionary.responseFailMesage }}
                </div>
                <div class="s-contact__msg__desc">
                  {{ dictionary.responseFailDescription }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios'

import Loader from './../components/common/Loader.vue'

import * as lib from './../../lib/lib'
import useVuelidate from '@vuelidate/core'

import { email, numeric, required } from '@vuelidate/validators'
import { v4 as uuidv4 } from 'uuid'

export default {
  components: {
    Loader 
  },
  setup() {
    return { v$: useVuelidate() }
  },

  data() {
    return {
      // default
      componentKey: uuidv4(),
      scrollOffsetY: 100,
      isLoading: false,
      errorMsgs: [],
      showResponse: false,
      showFailResponse: false,
      showContactForm: false,
      // page properties
      title: '',
      summary: '',
      label: '',
      buttonContactLabel: '',
      buttonCancelText: '',
      images: [],
      // form properties
      name: '',
      surname: '',
      email: '',
      phone: '',
      terms: false,
      // reCaptchaResponse: '',
      contactModal: null,
    }
  },

  computed: {
    btnText() {
      return this.showContactForm
        ? this.buttonCancelText
        : this.buttonContactLabel
    },
  },
  validations() {
    return {
      name: { required },
      surname: { required },
      email: { required, email },
      phone: { required, numeric },
      terms: { checked: (value) => value === true },
      // reCaptchaResponse: { required }
    }
  },

  mounted() {
    const {
      title,
      summary,
      label,
      buttonContactLabel,
      buttonCancelText,
      images,
    } = lib.getData('contactFormData')
    this.title = title
    this.summary = summary
    this.label = label
    this.buttonContactLabel = buttonContactLabel
    this.buttonCancelText = buttonCancelText
    this.images = images   
  },

  methods: {   
    hideSuccessMsg() {
      if (this.showResponse || this.showFailResponse) {
        this.showResponse = false
        this.showFailResponse = false
      }

      this.resetForm()
    },

    async callCaptcha() {
      /* eslint-disable no-undef */

      return new Promise((resolve, reject) => {
        const fetchAsyncData = async () => {
          try {
            const result = await grecaptcha.execute(
              this.configuration.reCaptchaPublicKey,
              {
                action: this.configuration.action,
              }
            )
            resolve(result)
          } catch (error) {
            reject(error)
          }
        }

        fetchAsyncData()
      })

      /* eslint-enable no-alert, no-console */
    },

    async onSubmitForm() {
      this.v$.$validate()

      const captchaToken = await this.callCaptcha()
      const formData = new FormData()

      if (!captchaToken) return

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          lib.scrollToValidation(this.scrollOffsetY)
        })
        return false
      }

      this.isLoading = true

      formData.append('name', this.name)
      formData.append('surname', this.surname)
      formData.append('email', this.email)
      formData.append('phone', this.phone)
      formData.append('terms', this.terms)
      formData.append('__RequestVerificationToken', this.configuration.token)
      formData.append('captchaTokenResponse', captchaToken)

      axios
        .post(this.configuration.endpointSubmitData, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        .then((response) => {
          const { hasError, errorMessages } = response.data
          // response has error
          if (hasError) {
            this.handleError(errorMessages)
            return false
          }
          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    handleSuccess() {
      this.errorMsgs = []
      this.isLoading = false
      this.showResponse = true
      this.showFailResponse = false    
      this.resetForm()

      this.v$.$reset()
    },

    handleError(messages) {      
      this.errorMsgs = messages
      this.isLoading = this.showResponse = false
      this.showFailResponse = true

      // this.$nextTick(() => {
      //   lib.scrollToError(this.scrollOffsetY)
      // })
    },

    resetForm() {
      this.name = ''
      this.surname = ''
      this.email = ''
      this.phone = ''
      this.terms = false
      this.v$.$reset()
      // this.reCaptchaResponse =""
    },
  },
}
</script>
